export type Tier1SleeveFillStatus = 'empty' | 'partial' | 'filled';

export type Tier1PlannedAction =
  | 'hold'
  | 'open'
  | 'add'
  | 'trim'
  | 'close'
  | 'pending_approval';

export type Tier1OrderUserDecision = 'pending' | 'approved' | 'denied';

export interface Tier1InvestmentPolicyStatement {
  title: string;
  policyVersion: number;
  frozenAt: string;
  policySnapshotHash: string;
  supersedeReason?: string;
  sections: {
    purpose: string;
    scope: string;
    macroAnchor: string;
    sleeveTargetsSummary: string;
    holdingsPolicy: string;
    thesisContext: string;
    constraints: string;
    rebalanceCadence: string;
    execution: string;
  };
  disclaimers: string[];
}

export type Tier1PlanRecord = Tier1Plan & { id?: string };

export interface Tier1Plan {
  _id?: string;
  id?: string;
  status: string;
  policyVersion?: number;
  policySnapshotHash?: string;
  policyFrozenAt?: string;
  macroAsOfMonth: string;
  weightsSource: 'rules' | 'ai';
  summary?: string;
  investmentPolicyStatement?: Tier1InvestmentPolicyStatement;
  sleeveTargets: Array<{ sleeveKey: string; name?: string; targetWeightPct: number }>;
  holdings: Array<{
    sleeveKey: string;
    symbol: string;
    targetWeightPct: number;
    role: string;
  }>;
  thesisContext?: {
    sixMonthsPlus?: { direction: string; confidence: number; generatedAt: string };
    oneMonth?: { direction: string; confidence: number; generatedAt: string };
  };
  risks?: string[];
  rationaleBySleeve?: Record<string, string>;
  planAuthor?: 'ai_review' | 'deterministic_fallback';
  accountContext?: string;
  macroAiWeightsId?: string;
  aiPlan?: {
    promptVersion: string;
    inputPacketHash?: string;
    model?: string | null;
    macroAiWeightsId?: string;
    planAuthor: 'ai_review' | 'deterministic_fallback';
    accountContext?: string;
    attempts?: number;
    fallbackReason?: string;
  };
}

export interface Tier1PolicyHistoryEntry {
  planId: string;
  policyVersion: number;
  status: string;
  frozenAt?: string;
  policySnapshotHash?: string;
  supersedeReason?: string;
  macroAsOfMonth: string;
  title?: string;
}

export interface Tier1HoldingRow {
  symbol: string;
  marketValue: number;
  quantity?: number;
  sleeveKey: string | null;
  inPlan: boolean;
  source?: 'schwab_account' | 'orchestrator';
  tierId?: string;
  positionId?: string;
  tagVerified?: boolean;
}

export interface Tier1SleeveRow {
  sleeveKey: string;
  name?: string;
  policyWeightPct: number;
  /** Active IPS weight when previewing a plan that would supersede the current one. */
  currentPolicyWeightPct?: number;
  actualWeightPct: number;
  /** Live macro cockpit target (% of portfolio), rules or AI per plan source. */
  cockpitWeightPct?: number;
  /** Macro neutral baseline (%), for tilt context. */
  neutralWeightPct?: number;
  deltaWeightPct: number;
  policyNotionalUsd?: number;
  actualNotionalUsd?: number;
  holdings: Tier1HoldingRow[];
  intendedSymbol?: string;
  positionId?: string;
  fillStatus?: Tier1SleeveFillStatus;
  needsAdditionalUsd?: number;
  tagVerified?: boolean;
  registryStatus?: string;
  plannedAction?: Tier1PlannedAction;
  attention?:
    | 'filled'
    | 'actionable_underfill'
    | 'untradeable_residual'
    | 'awaiting_approval'
    | 'auth_blocked'
    | 'permanently_failed'
    | 'in_flight';
  attentionReason?: string;
}

export interface Tier1ImplementationReport {
  tierLiveBudgetUsd?: number;
  tierPolicyBasisUsd?: number;
  tierBudgetUsd: number;
  tierDeployedUsd: number;
  tierAvailableUsd: number;
  capitalDriftUsd?: number;
  tierCashUsd?: number;
  sleeveTrackingErrorPct: number;
  hasActivePlan: boolean;
  planId?: string;
  registeredSlotCount?: number;
  sleeves: Tier1SleeveRow[];
  unmappedHoldings: Tier1HoldingRow[];
  planSymbolsNotHeld: string[];
  ips?: Tier1InvestmentPolicyStatement | null;
  policySnapshotHash?: string;
  policyIntegrityOk?: boolean;
  usingNeutralBaseline?: boolean;
}

export interface TierBudgetSnapshot {
  tierId: string;
  tierName: string;
  allocationPct: number;
  accountNavUsd: number;
  tierBudgetUsd: number;
  tierDeployedUsd: number;
  tierAvailableUsd: number;
  orchestratorDeployedUsd?: number;
  schwabEquityEtfUsd?: number;
}

export interface Tier1RebalanceProposal {
  _id: string;
  status: 'proposed' | 'approved' | 'executed' | 'skipped' | 'expired';
  proposalKind?: 'initial_fill' | 'monthly_rebalance' | 'plan_transition' | 'sleeve_repair';
  transitionWarnings?: string[];
  createdAt?: string;
  orders: Array<{
    symbol: string;
    side: string;
    quantity?: number;
    estNotionalUsd?: number;
    reason?: string;
    sleeveKey?: string;
    estimatedRealizedGainUsd?: number;
    userDecision?: Tier1OrderUserDecision;
  }>;
  macroDelta?: { sumAbsDelta?: number; asOfLabel?: string };
  taxEstimate?: {
    estimatedGainsUsd?: number;
    taxBudgetYtdUsd?: number;
    taxBudgetCapUsd?: number;
    clippedByTaxBudget?: boolean;
    turnoverUsd?: number;
    turnoverCapUsd?: number;
    clippedByTurnover?: boolean;
  };
  skippedReason?: string;
  quoteSkippedSymbols?: string[];
  executionErrors?: string[];
  executionPhase?: 'queued' | 'submitting' | 'monitoring' | 'complete';
  queuedAt?: string;
  stalledSince?: string;
  executionHold?: {
    reason: 'schwab_auth';
    message: string;
    since: string;
  };
  investorLetterSections?: Record<string, string>;
  tradesExecuted: boolean;
}

export interface Tier1TradeOrderLineStatus {
  status: string;
  schwabOrderId?: string;
  rejectionReason?: string;
}

export interface Tier1WorkbenchSummary {
  openSleeves: number;
  needOpen: number;
  needAdd: number;
  needClose: number;
  pendingApproval: number;
  hasActiveProposal: boolean;
  residualSleeves?: number;
  actionableSleeves?: number;
  authBlockedSleeves?: number;
  failedSleeves?: number;
  executionStalled?: boolean;
  executionHold?: {
    reason: 'schwab_auth';
    message: string;
    since: string;
  } | null;
}

export interface Tier1Workbench {
  implementation: Tier1ImplementationReport;
  activeProposal: Tier1RebalanceProposal | null;
  pendingApprovalProposal?: Tier1RebalanceProposal | null;
  recentProposals: Tier1RebalanceProposal[];
  tradeOrdersByLine: Record<string, Tier1TradeOrderLineStatus>;
  summary: Tier1WorkbenchSummary;
}

export function tier1PlanId(plan: Tier1PlanRecord | null | undefined): string {
  if (!plan) return '';
  return String(plan._id ?? plan.id ?? '');
}

export function normalizeTier1PlanList(raw: unknown): Tier1Plan[] {
  if (Array.isArray(raw)) return raw as Tier1Plan[];
  if (raw && typeof raw === 'object' && Array.isArray((raw as { plans?: unknown }).plans)) {
    return (raw as { plans: Tier1Plan[] }).plans;
  }
  return [];
}

export function findPendingTier1Plan(
  plansRaw: unknown,
  active: Tier1Plan | null,
): Tier1Plan | null {
  const plans = normalizeTier1PlanList(plansRaw);
  const activeId = tier1PlanId(active);
  return (
    plans.find(
      (p) =>
        (p.status === 'pending_approval' || p.status === 'draft') &&
        tier1PlanId(p) !== activeId,
    ) ?? null
  );
}
